import Skeleton from 'components/skeleton';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import { DealItem, PartnerDetail } from 'models/partner';
import {
  ReactElement, useContext, useEffect, useState,
} from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { getLaminasApiData } from 'services/api';
import { ProStatusLevel } from 'models/general';
import { StoreContext } from 'models/import';
import DealCards from 'components/dealCards';
import { randomShuffle } from 'services/util';
import Banner, { BannerModifier } from 'components/banner';
import ContentBlock, { ContentBlockModifier } from 'components/contentBlock';
import { DealModifier } from 'components/dealCard';
import Header from 'components/header';
import PartnerDetailsHeader from './partnerDetailsHeader';

function PartnerDetailsComponent() {
  const { userSettings } = useContext(StoreContext);
  const { name } = useParams();
  const [featuredDeals, setFeaturedDeals] = useState([] as DealItem[]);
  const [partnerDetails, setPartnerDetails] = useState<PartnerDetail | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  const getPartnerDetail = (_name: string) => getLaminasApiData<PartnerDetail>(`/benefits/partner/detail?name=${_name}`);
  const getDeals = () => getLaminasApiData<DealItem[]>('/benefits/deal/list');

  const getFeaturedDeals = (deals: DealItem[]): DealItem[] => {
    const topPartners = deals.filter((deal) => deal.partner?.top_partner);

    return randomShuffle(topPartners);
  };

  const location = useLocation();

  useEffect(() => {
    setIsLoading(true);
    window.scrollTo(0, 0);

    const partnerName = name || '';

    Promise.all([
      getPartnerDetail(partnerName),
      getDeals(),
    ])
      .then((res) => {
        const [partnerDataRes, dealsDataRes] = res;
        const partnerData = partnerDataRes.data;
        const dealsData = dealsDataRes.data;

        if (partnerData && partnerData.top_partner) {
          partnerData.deal = {
            ...partnerData.deals[0],
            partner: {
              name: partnerData.name,
              display_name: partnerData.display_name,
              logo_uri: partnerData.logo_uri,
              top_partner: partnerData.top_partner,
            },
          };
          setPartnerDetails(partnerData);
        } else {
          window.location.href = 'https://cointracking.info/404.php';
        }

        if (dealsData) {
          setFeaturedDeals(getFeaturedDeals(dealsData));
        }

        setIsLoading(false);
      }, () => {
        setIsLoading(true);
      });
  }, [location]);

  return (
    <>
      <div className="ct_section _primary">
        <div className="ct_section-inner _partnerDetails">
          <PartnerDetailsHeader
            title={t('CoinTracking Top Partner', { ns: 'partner' })}
            name={partnerDetails?.display_name || <Skeleton width="180px" height="54px" />}
            uri={partnerDetails?.uri || ''}
            btnText={t('Visit site', { ns: 'partner' })}
            deal={partnerDetails?.deal || null}
            isLoading={isLoading}
          />
        </div>
      </div>
      <div className="ct_sectionGroup">
        {!!partnerDetails && (
          <>
            {!!partnerDetails.about_text && !isLoading && (
              <div className="ct_section _padded">
                <div className="ct_section-inner _partnerDetails">
                  <div className="ct_partnerDetailsSection">
                    <div className="ct_partnerDetailsSection-main">
                      <ContentBlock title={`${t('About', { ns: 'partner' })} ${partnerDetails.display_name}`} modifiers={[ContentBlockModifier.MAIN, ContentBlockModifier.NO_MARGIN]}>
                        <div className="ct_wysiwyg">
                          {parse(partnerDetails.about_text) as ReactElement}
                        </div>
                      </ContentBlock>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!!partnerDetails.services_text && !isLoading && (
              <div className="ct_section _padded">
                <div className="ct_section-inner _partnerDetails">
                  <div className="ct_partnerDetailsSection">
                    <div className="ct_partnerDetailsSection-main">
                      <ContentBlock title={t('Services', { ns: 'partner' })} modifiers={[ContentBlockModifier.MAIN, ContentBlockModifier.NO_MARGIN]}>
                        <div className="ct_wysiwyg">
                          {parse(partnerDetails.services_text) as ReactElement}
                        </div>
                      </ContentBlock>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!!partnerDetails.platform_preview_image_uri
              && !!partnerDetails.platform_preview_image_uri.length && !isLoading
              && (
                <div className="ct_section _padded">
                  <div className="ct_section-inner _partnerDetails">
                    <div className="ct_partnerDetailsSection">
                      <div className="ct_partnerDetailsSection-main">
                        <img className="ct_partnerDetails-image" src={partnerDetails.platform_preview_image_uri} alt="" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
          </>
        )}

        {isLoading && Array.from(Array(2)).map((e, i) => (
          // Skeleton for text fields
          <div className="ct_section _padded" key={i}>
            <div className="ct_section-inner _partnerDetails">
              <div className="ct_partnerDetailsSection">
                <div className="ct_partnerDetailsSection-main">
                  <ContentBlock
                    title={<Skeleton height="32px" width="40%" />}
                    modifiers={[ContentBlockModifier.MAIN, ContentBlockModifier.NO_MARGIN]}
                  >
                    <>
                      {Array.from(Array(4)).map((el, j) => <div className="ct_p" key={j}><Skeleton height="19px" /></div>)}
                    </>
                  </ContentBlock>
                </div>
              </div>
            </div>
          </div>
        ))}
        {isLoading && (
          // Skeleton for image
          <div className="ct_section _padded">
            <div className="ct_section-inner _partnerDetails">
              <div className="ct_partnerDetailsSection">
                <div className="ct_partnerDetailsSection-main">
                  <div className="ct_p"><Skeleton height="400px" /></div>
                </div>
              </div>
            </div>
          </div>
        )}
        {userSettings.proStatus === ProStatusLevel.FREE && (
          <div className="ct_section _padded">
            <div className="ct_section-inner _partnerDetails">
              <Banner
                title={t('Premium Account', { ns: 'partner' })}
                headline={t('Do you want more exclusive benefits?', { ns: 'partner' })}
                subline={t('Upgrade your account to Premium', { ns: 'partner' })}
                btnText={t('Upgrade Now', { ns: 'partner' })}
                modifier={BannerModifier.HAS_MARGIN}
              />
            </div>
          </div>
        )}
      </div>
      <div className="ct_section _padded _secondary">
        <div className="ct_section-inner _partnerDetails">
          <Header
            title={t('Top Partners', { ns: 'partner' })}
            headline={t('CoinTracking Featured Deals', { ns: 'partner' })}
          />
          {featuredDeals && !!featuredDeals.length && (
            <DealCards
              items={featuredDeals}
              modifier={DealModifier.FEATURED}
              skeletons={[...Array.from(Array(8)).map((e, i) => <Skeleton key={i} height="470px" />)]}
            />
          )}
        </div>
      </div>
    </>

  );
}

export default PartnerDetailsComponent;
